import { useCallback, useEffect, useRef, useState } from 'react'
import { isValidCarrierMcDigits } from '@numeo/types-core'
import { McConflict, searchCarrierByMc } from './fmcsaApi'
import { CarrierSuggestion } from './types'

interface UseCarrierLookupOptions {
    debounceMs?: number
}

interface UseCarrierLookupReturn {
    suggestions: CarrierSuggestion[]
    isLoading: boolean
    error: string | null
    mcConflict: McConflict | null
    search: (mcNumber: string) => void
    clearSuggestions: () => void
}

export const useCarrierLookup = (options: UseCarrierLookupOptions = {}): UseCarrierLookupReturn => {
    const { debounceMs = 500 } = options

    const [suggestions, setSuggestions] = useState<CarrierSuggestion[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [mcConflict, setMcConflict] = useState<McConflict | null>(null)

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)

    const clearSuggestions = useCallback(() => {
        setSuggestions([])
        setError(null)
        setMcConflict(null)
    }, [])

    const search = useCallback(
        (mcNumber: string) => {
            // Clear previous timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }

            // Abort previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }

            const cleanMc = mcNumber.replace(/\D/g, '')

            // Only look up MC numbers the carrier profile can accept.
            if (!isValidCarrierMcDigits(cleanMc)) {
                setSuggestions([])
                setIsLoading(false)
                setError(null)
                setMcConflict(null)
                return
            }

            setIsLoading(true)
            setError(null)
            setMcConflict(null)

            // Debounce the search
            debounceTimerRef.current = setTimeout(async () => {
                abortControllerRef.current = new AbortController()

                try {
                    const { suggestions: results, mcConflict: conflict } = await searchCarrierByMc(cleanMc)
                    setSuggestions(results)
                    setMcConflict(conflict)

                    if (results.length === 0 && !conflict) {
                        setError('No carriers found for this MC number')
                    }
                } catch (err) {
                    if (err instanceof Error && err.name !== 'AbortError') {
                        setError('Failed to search carriers')
                        console.error('Carrier lookup error:', err)
                    }
                } finally {
                    setIsLoading(false)
                }
            }, debounceMs)
        },
        [debounceMs]
    )

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    return {
        suggestions,
        isLoading,
        error,
        mcConflict,
        search,
        clearSuggestions,
    }
}

export default useCarrierLookup
